import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { Selection } from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useOkapiKy } from '@folio/stripes/core';
import { translateReferenceValue } from '../../../../../../../helpers/referenceTableTranslations';

function ReferenceValueField({
  field,
  index,
  disabled,
  selectedMember,
  currentCubeMembers,
}) {
  const intl = useIntl();
  const okapiKy = useOkapiKy();
  const [referenceValues, setReferenceValues] = useState([]);
  const [loading, setLoading] = useState(false);

  // Get reference table info from member name - memoized to prevent re-renders
  const referenceTableInfo = useMemo(() => {
    if (!selectedMember) return null;

    const memberName = typeof selectedMember === 'string'
      ? selectedMember
      : selectedMember?.value || selectedMember;
    if (!memberName || typeof memberName !== 'string') return null;

    const [cubeName, fieldName] = memberName.split('.');

    // 1. Direct cube field key (aliasMember format: "material_type.name")
    //    cubeName is already the reference cube — use directly
    if (currentCubeMembers) {
      const cube = currentCubeMembers.find(c => c.name === cubeName);
      if (cube?.meta?.isReferenceTable === true) {
        return { cubeName, fieldName };
      }
    }

    // 2. Legacy view field name mapping ("cataloging_view.material_type_name")
    //    kept for backward compatibility with Advanced Query Builder
    const referenceFieldMapping = {
      'cataloging_view.instance_type_name':          { cubeName: 'instance_type',          fieldName: 'name' },
      'cataloging_view.instance_status_name':         { cubeName: 'instance_status',         fieldName: 'name' },
      'cataloging_view.mode_of_issuance_name_ref':    { cubeName: 'mode_of_issuance',        fieldName: 'name' },
      'cataloging_view.material_type_name':           { cubeName: 'material_type',           fieldName: 'name' },
      'cataloging_view.loan_type_name':               { cubeName: 'loan_type',               fieldName: 'name' },
      'cataloging_view.call_number_type_name':        { cubeName: 'call_number_type',        fieldName: 'name' },
      'cataloging_view.location_name_ref':            { cubeName: 'location',                fieldName: 'name' },
      'cataloging_view.loclibrary_name':              { cubeName: 'loclibrary',              fieldName: 'name' },
      'cataloging_view.loccampus_name':               { cubeName: 'loccampus',               fieldName: 'name' },
      'cataloging_view.locinstitution_name':          { cubeName: 'locinstitution',          fieldName: 'name' },
      'cataloging_view.contributor_type_name':        { cubeName: 'contributor_type',        fieldName: 'name' },
      'cataloging_view.contributor_name_type_name':   { cubeName: 'contributor_name_type',   fieldName: 'name' },
      'cataloging_view.identifier_type_name':         { cubeName: 'identifier_type',         fieldName: 'name' },
      'cataloging_view.classification_type_name':     { cubeName: 'classification_type',     fieldName: 'name' },
      'cataloging_view.note_type_name':               { cubeName: 'instance_note_type',      fieldName: 'name' },
      'cataloging_view.alternative_title_type_name':  { cubeName: 'alternative_title_type',  fieldName: 'name' },
      'cataloging_view.contributor_type_ref':         { cubeName: 'contributor_type',        fieldName: 'name' },
      'cataloging_view.identifier_type_ref':          { cubeName: 'identifier_type',         fieldName: 'name' },
      'cataloging_view.classification_type_ref':      { cubeName: 'classification_type',     fieldName: 'name' },
      'cataloging_view.note_type_ref':                { cubeName: 'instance_note_type',      fieldName: 'name' },
      'cataloging_view.statistical_code_name':        { cubeName: 'statistical_code',        fieldName: 'name' },
      'cataloging_view.statistical_code_type_name':   { cubeName: 'statistical_code_type',   fieldName: 'name' },
      'cataloging_view.instance_format_name':         { cubeName: 'instance_format',         fieldName: 'name' },
      'cataloging_view.nature_of_content_name':       { cubeName: 'nature_of_content_term',  fieldName: 'name' },
      'cataloging_view.alternative_title_type_ref':   { cubeName: 'alternative_title_type',  fieldName: 'name' },
    };

    if (referenceFieldMapping[memberName]) {
      return referenceFieldMapping[memberName];
    }

    // 3. Fallback: check cubeMetaData for Advanced Query Builder
    if (cubeMetaData?.response?.cubes) {
      const cube = cubeMetaData.response.cubes.find(c => c.name === cubeName);
      if (cube?.meta?.isReferenceTable === true) {
        return { cubeName, fieldName };
      }
    }

    return null;
  }, [selectedMember, currentCubeMembers]);

  // Fetch reference values via Okapi
  useEffect(() => {
    const fetchReferenceValues = async () => {
      if (!referenceTableInfo) {
        setReferenceValues([]);
        return;
      }

      setLoading(true);

      const cubeQuery = {
        dimensions: [
          `${referenceTableInfo.cubeName}.${referenceTableInfo.fieldName}`,
        ],
        measures: [],
        limit: 100,
      };

      try {
        const json = await okapiKy
          .post('report-execution', { json: { cubeQuery } })
          .json();

        const rawList = json?.data || json?.map?.data?.list || [];

        if (!rawList.length) {
          setReferenceValues([]);
          return;
        }

        const key = `${referenceTableInfo.cubeName}.${referenceTableInfo.fieldName}`;
        const tablePivot = rawList.map(row => ({ [key]: row[key] }));        const values = tablePivot
          .map((row) => {
            const fieldKey = `${referenceTableInfo.cubeName}.${referenceTableInfo.fieldName}`;
            const value = row[fieldKey];
            
            // Translate the value using the translation helper
            const translatedLabel = translateReferenceValue(
              intl,
              referenceTableInfo.cubeName,
              referenceTableInfo.fieldName,
              value
            );
            
            return {
              label: translatedLabel || value || 'N/A',
              value: value || '',
            };
          })
          .filter((item) => item.value); // Remove empty values

        setReferenceValues(values);
      } catch (error) {
        console.error('ReferenceValueField: Error loading reference values:', error.message);
        setReferenceValues([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReferenceValues();
  }, [referenceTableInfo, intl]);

  return (
    <Field
      id={`query-builder-form-referenceValueField-${index}`}
      component={Selection}
      name={`${field}.values`}
      label={
        <FormattedMessage
          id="ui-reports.filters.valueField.label"
          defaultMessage="Value"
        />
      }
      placeholder={intl.formatMessage({
        id: 'ui-reports.filters.valueField.selectPlaceholder',
        defaultMessage: 'Select value',
      })}
      dataOptions={referenceValues}
      disabled={disabled || loading}
      loading={loading}
    />
  );
}

ReferenceValueField.propTypes = {
  field: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  selectedMember: PropTypes.string,
  currentCubeMembers: PropTypes.array,
};

export default ReferenceValueField;
